import type { Control, FieldErrors, UseFormWatch } from "react-hook-form";
import { FormFieldController } from "../../../../../../../components/BuildingBlocks/Forms/Controllers/FormFieldController";
import { DueDateFields } from "../../../../../../../components/BuildingBlocks/Forms/FormFields/GroupedFields/DueDateFields/DueDateFields";
import { FormFieldInfoText } from "../../../../../../../components/BuildingBlocks/Forms/MantineFormFields/FormFieldInfoText/FormFieldInfoText";
import { SiteName } from "../../../../../../../utils/enums";
import type { Paragraph6ContractFormFields } from "../../../../../Paragraph6.types";
import { PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA } from "../Paragraph6ContractForm.constants";

declare const SITE_NAME: SiteName;

interface BeteiligungenProps {
  control: Control<Paragraph6ContractFormFields>;
  errors: FieldErrors<Paragraph6ContractFormFields>;
  isStaff?: boolean;
  watch: UseFormWatch<Paragraph6ContractFormFields>;
}

function Beteiligungen({
  control,
  errors,
  isStaff,
  watch
}: BeteiligungenProps) {
  let supportEmail = "";
  if (SITE_NAME === SiteName.Optinode) supportEmail = "support@node.energy";
  if (SITE_NAME === SiteName.Smarendo) supportEmail = "smarendo@node.energy";

  const FORMULA_SUPPORT_TEXT = `Hinweis: Der Formel-Bereich befindet sich derzeit in der Entwicklung. Aufgrund dessen ist eine Bearbeitung vorübergehend nicht möglich. Sollten Sie Änderungsbedarf haben, wenden Sie sich bitte an unseren Support${supportEmail !== "" ? ` unter [${supportEmail}](mailto:${supportEmail})` : ""}. Vielen Dank für Ihr Verständnis!`;

  const hasSplitBeteiligung = watch("has_split_beteiligung");
  const beteiligungFieldInfo = {
    ...PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.beteiligung,
    label: hasSplitBeteiligung
      ? "Beteiligung für rückerstattungsfähige Mengen in ct/kWh"
      : PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.beteiligung.label
  };
  const formulaBeteiligungInfo = {
    ...PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.formula_beteiligung,
    label: hasSplitBeteiligung
      ? "Formel für rückerstattungsfähige Mengen"
      : PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.formula_beteiligung.label,
    disabled: !isStaff
  };
  const formulaBeteiligungTextInfo = {
    ...PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.formula_beteiligung_text,
    disabled: !isStaff,
    infoText: isStaff
      ? PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.formula_beteiligung_text.infoText
      : undefined
  };
  const formulaBeteiligungNichtRueckerstattungsfaehigInfo = {
    ...PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.formula_beteiligung_nicht_erstattungsfaehig,
    disabled: !isStaff
  };
  const formulaBeteiligungNichtRueckerstattungsfaehigTextInfo = {
    ...PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.formula_beteiligung_nicht_erstattungsfaehig_text,
    disabled: !isStaff,
    infoText: isStaff
      ? PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA
          .formula_beteiligung_nicht_erstattungsfaehig_text.infoText
      : undefined
  };

  const has_split_beteiligung_field_info = {
    name: "has_split_beteiligung",
    required: true,
    label:
      "Für diesen Vertrag gelten gesonderte Regelungen zur Berücksichtigung der rückerstattungsfähigen Mengen",
    type: "boolean",
    helpText:
      "Ausschließlich für die tatsächlich eingespeisten Strommengen wird, je nach definiertem Abrechnungszeitraum, eine ordnungsgemäße Gutschrift für die Gemeinde erstellt."
  } as const;
  const has_fiktive_strommenge_field_info = {
    name: "has_fiktive_strommenge",
    required: true,
    label:
      "Für diesen Vertrag sollen fiktive Strommengen berücksichtigt werden.",
    type: "boolean",
    helpText: `Es gibt drei Arten von fiktiven Strommengen:

- Strommengen, die im Rahmen des Redispatch 2.0-Regimes abgeregelt wurden.

- Strommengen, die auf eine technische Nichtverfügbarkeit von mehr als 2 Prozent des Bruttostromertrags zurückgehen.

- Strommengen, die wegen sonstigen Abschaltungen oder Drosselungen, zum Beispiel der optimierten Vermarktung des Stroms, der Eigenversorgung oder der Stromlieferungen unmittelbar an Dritte, nicht eingespeist wurden.

Haben Sie ausgewählt, dass diese Mengen berücksichtigt werden, dann gehen Sie bitte anschließend wie folgt vor: Wechseln Sie in den Reiter Energiedaten und laden Sie dort die fiktiven Strommengen je Erzeuger hoch.`
  } as const;

  const has_fiktive_strommenge = watch("has_fiktive_strommenge");
  const formulaBeteiligung = watch("formula_beteiligung");
  const formulaBeteiligungText = watch("formula_beteiligung_text");
  const formulaBeteiligungNichtErstattungsfaehig = watch(
    "formula_beteiligung_nicht_erstattungsfaehig"
  );
  const formulaBeteiligungNichtErstattungsfaehigText = watch(
    "formula_beteiligung_nicht_erstattungsfaehig_text"
  );

  const showFormulaBeteiligungArea = isStaff || formulaBeteiligung;
  const showFormulaBeteiligungTextInfoField =
    formulaBeteiligung && (isStaff || formulaBeteiligungText);

  const showFormulaBeteiligungNichtRueckerstattungsfaehigArea =
    isStaff || formulaBeteiligungNichtErstattungsfaehig;
  const showFormulaBeteiligungNichtRueckerstattungsfaehigTextInfoField =
    formulaBeteiligungNichtErstattungsfaehig &&
    (isStaff || formulaBeteiligungNichtErstattungsfaehigText);

  return (
    <section>
      <FormFieldInfoText infoText="Die vertragliche Beteiligung der Gemeinde ist pro Strommenge auf jeweils maximal 0,2 begrenzt." />
      <FormFieldController
        control={control}
        data={has_split_beteiligung_field_info}
      />
      <FormFieldController control={control} data={beteiligungFieldInfo} />
      {showFormulaBeteiligungArea && (
        <>
          <FormFieldController
            control={control}
            data={formulaBeteiligungInfo}
          />
          {showFormulaBeteiligungTextInfoField && (
            <FormFieldController
              control={control}
              data={formulaBeteiligungTextInfo}
            />
          )}
          {!isStaff && <FormFieldInfoText infoText={FORMULA_SUPPORT_TEXT} />}
        </>
      )}
      {hasSplitBeteiligung && (
        <>
          <FormFieldController
            control={control}
            data={
              PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.beteiligung_nicht_erstattungsfaehig
            }
          />
          {showFormulaBeteiligungNichtRueckerstattungsfaehigArea && (
            <>
              <FormFieldController
                control={control}
                data={formulaBeteiligungNichtRueckerstattungsfaehigInfo}
              />
              {showFormulaBeteiligungNichtRueckerstattungsfaehigTextInfoField && (
                <FormFieldController
                  control={control}
                  data={formulaBeteiligungNichtRueckerstattungsfaehigTextInfo}
                />
              )}
              {!isStaff && (
                <FormFieldInfoText infoText={FORMULA_SUPPORT_TEXT} />
              )}
            </>
          )}
        </>
      )}
      <FormFieldController
        control={control}
        data={has_fiktive_strommenge_field_info}
      />
      {has_fiktive_strommenge && (
        <FormFieldController
          control={control}
          data={
            PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.beteiligung_fiktive_strommengen
          }
        />
      )}
      <FormFieldController
        control={control}
        data={PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA.payment_method}
      />
      <DueDateFields
        allowNull
        control={control}
        errors={errors}
        formFieldData={PARAGRAPH_6_CONTRACT_FORM_INPUT_DATA}
        label="Fälligkeit der Zahlung"
        prefix="in"
        unitFieldName="due_date_unit"
        valueFieldName="due_date_value"
      />
    </section>
  );
}

export { Beteiligungen };
